import * as React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSubscription } from '@apollo/client';
import clsx from 'clsx';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { markdownSanitizeSchema } from '~/lib/markdownSanitizeSchema';
import {
  ChevronLeftIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { motion } from 'motion/react';
import { Spinner } from '@oysterjs/ui/spinner';
import { AnimateIn } from '@oysterjs/ui/transition';
import { client } from '~/api/client';
import {
  useGetWorkflowExecutionQuery,
  WatchWorkflowExecutionDocument,
  WatchWorkflowExecutionSubscription,
  WatchWorkflowExecutionSubscriptionVariables,
  WorkflowExecutionState,
  WorkflowTaskState,
  WorkflowTaskEventType,
  WorkflowArtifactType
} from '~/api/graphql';
import {
  computeDepthGroups,
  computeDAGEdges,
  formatDefinitionKey,
  formatDuration,
  formatElapsedLive,
  DEFINITION_LABELS,
  executionStateLabel
} from './utils';

function checkAtBottom(e: React.UIEvent<HTMLDivElement>, ref: React.MutableRefObject<boolean>) {
  const el = e.currentTarget;
  ref.current = el.scrollHeight - el.scrollTop < el.clientHeight + 24;
}

type SubscriptionExecution = WatchWorkflowExecutionSubscription['watchWorkflowExecution'];
type SubscriptionTask = SubscriptionExecution['tasks'][number];
type SubscriptionEvent = SubscriptionTask['events'][number];
type ExecutionOutput = SubscriptionExecution['outputs'][number];

type DisplayArtifact = {
  id: string;
  name: string;
  artifact: NonNullable<ExecutionOutput['artifact']>;
};

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

const DetailHeader = (props: { execution: SubscriptionExecution | null; id: string }) => {
  const { execution, id } = props;
  const state = execution?.state;
  const stateInfo = executionStateLabel(state ?? 'QUEUED');

  const isTerminal =
    state === WorkflowExecutionState.Completed ||
    state === WorkflowExecutionState.Failed ||
    state === WorkflowExecutionState.Cancelled;

  const duration = isTerminal ? formatDuration(execution?.startedAt, execution?.completedAt) : null;

  return (
    <div className="sticky top-0 z-10 bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 h-14 px-8 flex items-center gap-4">
      <Link
        to="/app/workflows/executions"
        className="rounded-full py-1.5 pl-2 pr-3 flex items-center gap-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-400 text-xs transition-colors duration-150"
      >
        <ChevronLeftIcon className="size-4" />
        Executions
      </Link>

      <div className="h-4 w-px bg-neutral-200 dark:bg-neutral-700" />

      <span className="text-sm font-medium text-neutral-900 dark:text-white">
        {DEFINITION_LABELS[execution ? 'default' : ''] ?? 'Workflow Execution'}
      </span>

      <span
        className={clsx(
          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
          stateInfo.className
        )}
      >
        {stateInfo.dotClassName && (
          <span className={clsx('mr-1.5 h-1.5 w-1.5 rounded-full', stateInfo.dotClassName)} />
        )}
        {stateInfo.text}
      </span>

      {duration && (
        <span className="text-xs tabular-nums text-neutral-500 dark:text-neutral-400">
          {duration}
        </span>
      )}

      {!isTerminal && execution?.startedAt && <LiveDuration startedAt={execution.startedAt} />}

      <div className="flex-1" />

      <span
        className="text-xs font-mono text-neutral-400 dark:text-neutral-500 select-all truncate max-w-[200px]"
        title={id}
      >
        {id}
      </span>
    </div>
  );
};

const LiveDuration = (props: { startedAt: Date }) => {
  const [elapsed, setElapsed] = React.useState(() => formatElapsedLive(props.startedAt));

  React.useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(formatElapsedLive(props.startedAt));
    }, 1000);
    return () => clearInterval(interval);
  }, [props.startedAt]);

  if (!elapsed) return null;
  return (
    <span className="text-xs tabular-nums text-neutral-500 dark:text-neutral-400">{elapsed}</span>
  );
};

// ---------------------------------------------------------------------------
// Sub-navigation tabs
// ---------------------------------------------------------------------------

const SubNavTabs = (props: {
  activeTab: string;
  onTabChange: (tab: string) => void;
  artifacts: DisplayArtifact[];
}) => {
  return (
    <div className="flex-shrink-0 px-8 bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800">
      <nav className="-mb-px flex space-x-6">
        <TabButton
          label="Workflow"
          active={props.activeTab === 'workflow'}
          onClick={() => props.onTabChange('workflow')}
        />
        {props.artifacts.map((a) => {
          const tabId = `output-${a.id}`;
          return (
            <AnimateIn key={tabId}>
              <TabButton
                label={a.name}
                active={props.activeTab === tabId}
                onClick={() => props.onTabChange(tabId)}
              />
            </AnimateIn>
          );
        })}
      </nav>
    </div>
  );
};

const TabButton = (props: { label: string; active: boolean; onClick: () => void }) => (
  <button
    onClick={props.onClick}
    className={clsx(
      'border-b-2 px-1 py-3 text-sm font-medium whitespace-nowrap transition-colors duration-150',
      props.active
        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 hover:border-neutral-300 dark:hover:border-neutral-600'
    )}
  >
    {props.label}
  </button>
);

// ---------------------------------------------------------------------------
// Task Pipeline
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// DAG helpers
// ---------------------------------------------------------------------------

type SubStepProgress = { title: string; percent: number; message: string };

function getSubStepProgress(events: readonly SubscriptionEvent[]): SubStepProgress[] {
  const latest = new Map<string, { seq: number; percent: number; message: string }>();
  for (const ev of events) {
    if (!ev.progress) continue;
    const prev = latest.get(ev.title);
    if (!prev || ev.progress.percent > prev.percent) {
      latest.set(ev.title, {
        seq: ev.sequence,
        percent: ev.progress.percent,
        message: ev.progress.message
      });
    }
  }

  return Array.from(latest.entries()).map(([title, v]) => ({
    title,
    percent: v.percent,
    message: v.message
  }));
}

function getCumulativePercent(steps: SubStepProgress[]): number {
  if (steps.length === 0) return 0;
  return steps.reduce((sum, s) => sum + s.percent, 0) / steps.length;
}

// ---------------------------------------------------------------------------
// DAG Layout – computed fixed-grid positions (no DOM measurement)
// ---------------------------------------------------------------------------

const NODE_W = 192;
const NODE_H = 36;
const COL_GAP = 16;
const ROW_GAP = 52;

type NodePos = { x: number; y: number };

function computeDAGLayout(
  depthGroups: { depth: number; tasks: { id: string }[] }[],
  edges: { from: string; to: string }[]
): { positions: Map<string, NodePos>; width: number; height: number } {
  if (depthGroups.length === 0) return { positions: new Map(), width: 0, height: 0 };

  const STEP = NODE_W + COL_GAP;

  const adj = new Map<string, string[]>();
  for (const e of edges) {
    if (!adj.has(e.from)) adj.set(e.from, []);
    adj.get(e.from)!.push(e.to);
    if (!adj.has(e.to)) adj.set(e.to, []);
    adj.get(e.to)!.push(e.from);
  }

  let widestN = 0;
  for (const g of depthGroups) widestN = Math.max(widestN, g.tasks.length);
  const anchorW = (widestN - 1) * STEP;

  const xOf = new Map<string, number>();
  for (const group of depthGroups) {
    const n = group.tasks.length;
    const layerW = (n - 1) * STEP;
    const offset = (anchorW - layerW) / 2;
    group.tasks.forEach((t, j) => xOf.set(t.id, offset + j * STEP));
  }

  for (let iter = 0; iter < 8; iter++) {
    for (const group of depthGroups) {
      const tasks = group.tasks;
      if (tasks.length === 0) continue;

      const targets = new Map<string, number>();
      for (const t of tasks) {
        const nbrs = adj.get(t.id);
        if (!nbrs || nbrs.length === 0) {
          targets.set(t.id, xOf.get(t.id)!);
        } else {
          targets.set(t.id, nbrs.reduce((s, n) => s + (xOf.get(n) ?? 0), 0) / nbrs.length);
        }
      }

      const sorted = [...tasks].sort((a, b) => (targets.get(a.id) ?? 0) - (targets.get(b.id) ?? 0));
      for (const t of sorted) xOf.set(t.id, targets.get(t.id)!);

      if (sorted.length > 1) {
        let needsSpread = false;
        for (let i = 1; i < sorted.length; i++) {
          if (xOf.get(sorted[i].id)! - xOf.get(sorted[i - 1].id)! < STEP) {
            needsSpread = true;
            break;
          }
        }
        if (needsSpread) {
          const centroid = sorted.reduce((s, t) => s + (xOf.get(t.id) ?? 0), 0) / sorted.length;
          const totalW = (sorted.length - 1) * STEP;
          sorted.forEach((t, i) => xOf.set(t.id, centroid - totalW / 2 + i * STEP));
        }
      }
    }
  }

  let minX = Infinity;
  for (const x of xOf.values()) minX = Math.min(minX, x);
  for (const [id, x] of xOf) xOf.set(id, x - minX);

  let maxRight = 0;
  for (const x of xOf.values()) maxRight = Math.max(maxRight, x + NODE_W);

  const totalW = Math.max(maxRight, NODE_W);
  const totalH = depthGroups.length * NODE_H + Math.max(depthGroups.length - 1, 0) * ROW_GAP;

  const positions = new Map<string, NodePos>();
  depthGroups.forEach((group, rowIdx) => {
    const y = rowIdx * (NODE_H + ROW_GAP);
    for (const t of group.tasks) positions.set(t.id, { x: xOf.get(t.id)!, y });
  });

  return { positions, width: totalW, height: totalH };
}

function edgePath(from: NodePos, to: NodePos): string {
  const x1 = from.x + NODE_W / 2;
  const y1 = from.y + NODE_H;
  const x2 = to.x + NODE_W / 2;
  const y2 = to.y;
  const dy = y2 - y1;
  return `M ${x1} ${y1} C ${x1} ${y1 + dy * 0.4}, ${x2} ${y2 - dy * 0.4}, ${x2} ${y2}`;
}

// ---------------------------------------------------------------------------
// ProgressRing
// ---------------------------------------------------------------------------

const RING_SIZE = 18;
const RING_STROKE = 2;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const ProgressRing = (props: { percent: number }) => {
  const offset = RING_CIRCUMFERENCE * (1 - Math.min(props.percent, 1));
  return (
    <svg width={RING_SIZE} height={RING_SIZE} className="shrink-0 -rotate-90">
      <circle
        cx={RING_SIZE / 2}
        cy={RING_SIZE / 2}
        r={RING_RADIUS}
        fill="none"
        stroke="currentColor"
        strokeWidth={RING_STROKE}
        className="text-neutral-200 dark:text-neutral-700"
      />
      <circle
        cx={RING_SIZE / 2}
        cy={RING_SIZE / 2}
        r={RING_RADIUS}
        fill="none"
        stroke="currentColor"
        strokeWidth={RING_STROKE}
        strokeDasharray={RING_CIRCUMFERENCE}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="text-blue-400 transition-all duration-700 ease-out"
      />
    </svg>
  );
};

// ---------------------------------------------------------------------------
// TaskNode – fixed-width card
// ---------------------------------------------------------------------------

const TaskNode = (props: { task: SubscriptionTask; isSelected: boolean; onSelect: () => void }) => {
  const { task, isSelected } = props;
  const isRunning = task.state === WorkflowTaskState.Running;
  const isCompleted = task.state === WorkflowTaskState.Completed;
  const isFailed = task.state === WorkflowTaskState.Failed;
  const isPending = task.state === WorkflowTaskState.Pending;
  const isSkipped = task.state === WorkflowTaskState.Skipped;

  const subSteps = React.useMemo(() => getSubStepProgress(task.events ?? []), [task.events]);
  const cumulativePercent = isCompleted ? 1 : getCumulativePercent(subSteps);
  const duration = formatDuration(task.startedAt, task.completedAt);

  return (
    <motion.button
      onClick={props.onSelect}
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{
        opacity: 1,
        scale: 1,
        boxShadow: isRunning
          ? [
              '0 1px 2px rgba(0,0,0,0.05), 0 0 0 0 rgba(96,165,250,0)',
              '0 1px 2px rgba(0,0,0,0.05), 0 0 0 3px rgba(96,165,250,0.12)',
              '0 1px 2px rgba(0,0,0,0.05), 0 0 0 0 rgba(96,165,250,0)'
            ]
          : '0 1px 2px rgba(0,0,0,0.05)'
      }}
      transition={{
        opacity: { duration: 0.3 },
        scale: { duration: 0.3 },
        boxShadow: isRunning
          ? { duration: 2, repeat: Infinity, ease: 'easeInOut' }
          : { duration: 0.3 }
      }}
      className={clsx(
        'w-full h-full flex items-center gap-2 rounded-lg px-3 text-left',
        'border transition-colors duration-150',
        isPending && 'bg-white dark:bg-neutral-800/60 border-neutral-200 dark:border-neutral-700',
        isRunning && 'bg-white dark:bg-neutral-800 border-blue-200 dark:border-blue-800/60',
        isCompleted && 'bg-white dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700',
        isFailed && 'bg-white dark:bg-neutral-800 border-red-200 dark:border-red-800/60',
        isSkipped &&
          'bg-neutral-50 dark:bg-neutral-800/30 border-neutral-200 dark:border-neutral-700 opacity-50',
        isSelected && 'ring-2 ring-primary-500 dark:ring-primary-400'
      )}
    >
      {isRunning ? (
        <ProgressRing percent={cumulativePercent} />
      ) : (
        <TaskStateIcon state={task.state} />
      )}

      <span
        className={clsx(
          'text-sm font-medium capitalize truncate flex-1',
          isPending || isSkipped
            ? 'text-neutral-400 dark:text-neutral-500'
            : 'text-neutral-800 dark:text-neutral-100',
          isSkipped && 'line-through'
        )}
      >
        {formatDefinitionKey(task.definitionKey)}
      </span>

      {isCompleted && duration && (
        <span className="text-xs tabular-nums text-neutral-400 dark:text-neutral-500 shrink-0">
          {duration}
        </span>
      )}
      {isFailed && <span className="h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" />}
    </motion.button>
  );
};

const TaskStateIcon = (props: { state: WorkflowTaskState }) => {
  switch (props.state) {
    case WorkflowTaskState.Running:
      return <span className="h-2 w-2 rounded-full bg-blue-400 animate-pulse shrink-0" />;
    case WorkflowTaskState.Completed:
      return <CheckCircleIcon className="size-4 text-emerald-500 dark:text-emerald-400 shrink-0" />;
    case WorkflowTaskState.Failed:
      return <ExclamationCircleIcon className="size-4 text-red-500 dark:text-red-400 shrink-0" />;
    default:
      return <span className="h-2 w-2 rounded-full bg-neutral-300 dark:bg-neutral-600 shrink-0" />;
  }
};

// ---------------------------------------------------------------------------
// TaskDAG – computed layout with SVG edge paths
// ---------------------------------------------------------------------------

const TaskDAG = (props: {
  tasks: SubscriptionTask[];
  selectedTaskId: string | null;
  onSelectTask: (taskId: string | null) => void;
}) => {
  const depthGroups = React.useMemo(() => computeDepthGroups(props.tasks), [props.tasks]);
  const edges = React.useMemo(() => computeDAGEdges(props.tasks), [props.tasks]);
  const layout = React.useMemo(() => computeDAGLayout(depthGroups, edges), [depthGroups, edges]);

  if (props.tasks.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center">
        <Spinner size="size-6" color="neutral" />
        <p className="mt-3 text-sm text-neutral-400 dark:text-neutral-500">
          Waiting for workflow to start...
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 overflow-auto p-6">
      <div className="relative mx-auto" style={{ width: layout.width, height: layout.height }}>
        <svg
          className="absolute inset-0 pointer-events-none"
          width={layout.width}
          height={layout.height}
        >
          {edges.map((edge) => {
            const from = layout.positions.get(edge.from);
            const to = layout.positions.get(edge.to);
            if (!from || !to) return null;
            return (
              <path
                key={`${edge.from}-${edge.to}`}
                d={edgePath(from, to)}
                fill="none"
                className="stroke-neutral-300 dark:stroke-neutral-600"
                strokeWidth={2}
              />
            );
          })}
        </svg>

        {props.tasks.map((task) => {
          const pos = layout.positions.get(task.id);
          if (!pos) return null;
          return (
            <div
              key={task.id}
              className="absolute"
              style={{ left: pos.x, top: pos.y, width: NODE_W, height: NODE_H }}
            >
              <TaskNode
                task={task}
                isSelected={props.selectedTaskId === task.id}
                onSelect={() =>
                  props.onSelectTask(props.selectedTaskId === task.id ? null : task.id)
                }
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Task Detail Pane
// ---------------------------------------------------------------------------

const TaskDetailPane = (props: {
  task: SubscriptionTask;
  allTasks: SubscriptionTask[];
  onClose: () => void;
}) => {
  const { task, allTasks } = props;
  const isCompleted = task.state === WorkflowTaskState.Completed;

  const subSteps = React.useMemo(() => getSubStepProgress(task.events ?? []), [task.events]);
  const cumulativePercent = isCompleted ? 1 : getCumulativePercent(subSteps);
  const progressPercent = cumulativePercent * 100;

  const depTasks = React.useMemo(() => {
    const depIds = new Set(task.dependencies.map((d) => d.id));
    return allTasks.filter((t) => depIds.has(t.id));
  }, [task.dependencies, allTasks]);

  const eventsRef = React.useRef<HTMLDivElement>(null);
  const isEventsAtBottom = React.useRef(true);

  React.useEffect(() => {
    if (isEventsAtBottom.current && eventsRef.current) {
      eventsRef.current.scrollTop = eventsRef.current.scrollHeight;
    }
  }, [task.events?.length]);

  return (
    <div className="border-l border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 overflow-y-auto w-[400px] flex-shrink-0">
      <div className="sticky top-0 z-10 bg-white dark:bg-neutral-900 border-b border-neutral-100 dark:border-neutral-800 px-5 py-4 flex items-center gap-3">
        <span className="text-sm font-semibold text-neutral-900 dark:text-white capitalize">
          {formatDefinitionKey(task.definitionKey)}
        </span>
        <PaneStateBadge state={task.state} />
        <div className="flex-1" />
        <button
          onClick={props.onClose}
          className="rounded-full p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
        >
          <XMarkIcon className="size-4 text-neutral-400 dark:text-neutral-500" />
        </button>
      </div>

      <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
        {/* Progress */}
        <PaneSection title="Progress">
          <div className="flex items-center gap-4">
            <span className="text-2xl tabular-nums font-semibold text-neutral-900 dark:text-white">
              {Math.round(progressPercent)}%
            </span>
            <div className="flex-1 h-2 rounded-full bg-neutral-100 dark:bg-neutral-700 overflow-hidden">
              <div
                className={clsx(
                  'h-full transition-all duration-700 ease-out rounded-full',
                  isCompleted ? 'bg-emerald-400' : 'bg-blue-400'
                )}
                style={{ width: `${Math.min(progressPercent, 100)}%` }}
              />
            </div>
          </div>
          {subSteps.length > 1 && (
            <div className="mt-3 space-y-2">
              {subSteps.map((step) => (
                <div key={step.title}>
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs text-neutral-600 dark:text-neutral-400 truncate min-w-0">
                      {step.title}
                    </span>
                    {step.message && (
                      <span className="text-xs text-neutral-400 dark:text-neutral-500 truncate shrink-0">
                        {step.message.slice(0, 20)}
                      </span>
                    )}
                    <span className="text-xs tabular-nums text-neutral-400 dark:text-neutral-500 ml-auto shrink-0">
                      {Math.round(step.percent * 100)}%
                    </span>
                  </div>
                  <div className="mt-1 h-1 rounded-full bg-neutral-100 dark:bg-neutral-700 overflow-hidden">
                    <div
                      className="h-full bg-blue-400 transition-all duration-500 ease-out rounded-full"
                      style={{ width: `${Math.min(step.percent * 100, 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          {task.startedAt && (
            <div className="mt-2 text-xs text-neutral-400 dark:text-neutral-500 space-x-2">
              <span>
                Started{' '}
                {new Intl.DateTimeFormat('en-US', {
                  timeStyle: 'medium'
                }).format(new Date(task.startedAt))}
              </span>
              {task.completedAt && (
                <>
                  <span>·</span>
                  <span>Duration {formatDuration(task.startedAt, task.completedAt)}</span>
                </>
              )}
            </div>
          )}
        </PaneSection>

        {/* Dependencies */}
        {depTasks.length > 0 && (
          <PaneSection title="Dependencies">
            <div className="space-y-1.5">
              {depTasks.map((dep) => (
                <div key={dep.id} className="flex items-center gap-2 text-xs">
                  <TaskStateIcon state={dep.state} />
                  <span className="text-neutral-700 dark:text-neutral-300 capitalize">
                    {formatDefinitionKey(dep.definitionKey)}
                  </span>
                  {dep.completedAt && dep.startedAt && (
                    <span className="text-neutral-400 dark:text-neutral-500 tabular-nums">
                      {formatDuration(dep.startedAt, dep.completedAt)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </PaneSection>
        )}

        {/* Events */}
        <PaneSection title="Events">
          <div ref={eventsRef} onScroll={(e) => checkAtBottom(e, isEventsAtBottom)}>
            {(task.events?.length ?? 0) === 0 && (
              <p className="text-xs text-neutral-400 dark:text-neutral-500">No events yet.</p>
            )}
            <EventGroups events={task.events ?? []} />
          </div>
        </PaneSection>

        {/* Output */}
        {task.outputs.length > 0 && (
          <PaneSection title="Output">
            <div className="space-y-3">
              {task.outputs
                .slice()
                .sort((a, b) => a.sequence - b.sequence)
                .map((output) => {
                  const data = output.json?.data ?? output.text ?? output.artifact?.name ?? null;
                  if (data == null) return null;
                  const json = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
                  return (
                    <div key={output.id}>
                      <p className="text-xs font-mono text-neutral-500 dark:text-neutral-400 mb-1">
                        {output.key}
                      </p>
                      <pre className="text-xs font-mono bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg p-3 overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap break-words text-neutral-700 dark:text-neutral-300">
                        {json}
                      </pre>
                    </div>
                  );
                })}
            </div>
          </PaneSection>
        )}

        {/* Error */}
        {task.error && (
          <PaneSection title="Error">
            <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/50 rounded-lg p-4">
              <p className="text-sm text-red-700 dark:text-red-300">{task.error.message}</p>
              {task.error.code && (
                <p className="mt-1 text-xs font-mono text-red-500 dark:text-red-400">
                  {task.error.code}
                </p>
              )}
            </div>
          </PaneSection>
        )}
      </div>
    </div>
  );
};

const PaneSection = (props: { title: string; children: React.ReactNode }) => (
  <div className="p-5">
    <h3 className="text-xs font-medium uppercase tracking-wider text-neutral-400 dark:text-neutral-500 mb-3">
      {props.title}
    </h3>
    {props.children}
  </div>
);

const PaneStateBadge = (props: { state: WorkflowTaskState }) => {
  const info = (() => {
    switch (props.state) {
      case WorkflowTaskState.Running:
        return {
          text: 'Running',
          cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
        };
      case WorkflowTaskState.Completed:
        return {
          text: 'Completed',
          cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
        };
      case WorkflowTaskState.Failed:
        return {
          text: 'Failed',
          cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
        };
      case WorkflowTaskState.Skipped:
        return {
          text: 'Skipped',
          cls: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300'
        };
      default:
        return {
          text: 'Pending',
          cls: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300'
        };
    }
  })();
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        info.cls
      )}
    >
      {info.text}
    </span>
  );
};

type EventGroup = { title: string; events: SubscriptionEvent[] };

function groupEventsByTitle(events: readonly SubscriptionEvent[]): EventGroup[] {
  // Group events by title, regardless of order or interleaving
  const byTitle = new Map<string, EventGroup>();
  for (const ev of events) {
    if (ev.type === WorkflowTaskEventType.Progress) continue;
    if (!byTitle.has(ev.title)) {
      byTitle.set(ev.title, { title: ev.title, events: [] });
    }
    byTitle.get(ev.title)!.events.push(ev);
  }
  // Preserve the order in which each group's *first* event appears
  const titleFirstIdx = new Map<string, number>();
  for (let i = 0; i < events.length; i++) {
    const ev = events[i];
    if (ev.type === WorkflowTaskEventType.Progress) continue;
    if (!titleFirstIdx.has(ev.title)) {
      titleFirstIdx.set(ev.title, i);
    }
  }
  const groups = Array.from(byTitle.values());
  groups.sort((a, b) => titleFirstIdx.get(a.title)! - titleFirstIdx.get(b.title)!);
  return groups;
}

function eventDetail(ev: SubscriptionEvent): string | null {
  return (
    ev.message?.content ??
    ev.thinking?.content ??
    ev.progress?.message ??
    ev.error?.message ??
    (ev.retrieval ? `${ev.retrieval.query} (${ev.retrieval.resultCount})` : null)
  );
}

function formatTs(d: Date): string {
  return new Date(d).toLocaleTimeString('en-US', { hour12: false });
}

const EventGroups = (props: { events: readonly SubscriptionEvent[] }) => {
  const groups = React.useMemo(() => groupEventsByTitle(props.events), [props.events]);
  if (groups.length === 0) return null;

  return (
    <div className="space-y-2">
      {groups.map((group) => {
        const first = group.events[0];
        const last = group.events[group.events.length - 1];
        const isThinking = first.type === WorkflowTaskEventType.Thinking;
        const hasError = group.events.some((e) => !!e.error?.message);
        const ts = formatTs(first.createdAt);

        if (group.events.length === 1) {
          const detail = eventDetail(first);
          return (
            <div key={first.id}>
              <div className="flex items-baseline justify-between gap-2">
                <span
                  className={clsx(
                    'text-sm font-medium truncate min-w-0',
                    hasError
                      ? 'text-red-600 dark:text-red-400'
                      : isThinking
                        ? 'text-neutral-400 dark:text-neutral-500 italic'
                        : 'text-neutral-700 dark:text-neutral-200'
                  )}
                >
                  {group.title}
                </span>
                <span className="ml-auto text-xs text-neutral-400 dark:text-neutral-500 tabular-nums shrink-0">
                  {ts}
                </span>
              </div>
              {detail && (
                <p
                  className={clsx(
                    'text-xs mt-0.5',
                    first.error?.message
                      ? 'text-red-500 dark:text-red-400'
                      : 'text-neutral-400 dark:text-neutral-500'
                  )}
                >
                  {detail.slice(0, 250)}
                  {detail.length > 250 ? '…' : ''}
                </p>
              )}
            </div>
          );
        }

        const lastTs = formatTs(last.createdAt);
        return (
          <div key={first.id}>
            <div className="flex items-baseline justify-between gap-2">
              <span
                className={clsx(
                  'text-sm font-medium truncate min-w-0',
                  hasError
                    ? 'text-red-600 dark:text-red-400'
                    : isThinking
                      ? 'text-neutral-400 dark:text-neutral-500 italic'
                      : 'text-neutral-700 dark:text-neutral-200'
                )}
              >
                {group.title}
              </span>
              <span className="ml-auto text-xs text-neutral-400 dark:text-neutral-500 tabular-nums shrink-0">
                {lastTs}
              </span>
            </div>
            <div className="mt-1 space-y-0.5">
              {group.events.map((ev) => {
                const detail = eventDetail(ev);
                return (
                  <div key={ev.id} className="py-0.5">
                    <div className="flex items-baseline gap-2">
                      {detail && (
                        <p
                          className={clsx(
                            'min-w-0 text-xs',
                            ev.error?.message
                              ? 'text-red-500 dark:text-red-400'
                              : 'text-neutral-400 dark:text-neutral-500'
                          )}
                        >
                          {detail.slice(0, 250)}
                          {detail.length > 250 ? '…' : ''}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Output tab content
// ---------------------------------------------------------------------------

const OutputTabContent = (props: { artifact: DisplayArtifact }) => {
  const artifact = props.artifact.artifact;

  if (artifact.type === WorkflowArtifactType.Markdown && artifact.markdown) {
    return (
      <div className="p-8 prose prose-neutral dark:prose-invert max-w-none">
        <Markdown
          remarkPlugins={[[remarkGfm, {}]]}
          rehypePlugins={[
            [rehypeRaw, { tagFilter: true }],
            [rehypeSanitize, markdownSanitizeSchema]
          ]}
        >
          {artifact.markdown.content}
        </Markdown>
      </div>
    );
  }

  if (artifact.type === WorkflowArtifactType.File && artifact.file) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-6 text-center max-w-sm">
          <p className="text-sm font-medium text-neutral-900 dark:text-white">{artifact.name}</p>
          <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
            {artifact.file.mimeType}
          </p>
          {artifact.file.url && (
            <a
              href={artifact.file.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-600 transition-colors"
            >
              Download
            </a>
          )}
        </div>
      </div>
    );
  }

  return null;
};

// ---------------------------------------------------------------------------
// Loading / Error states
// ---------------------------------------------------------------------------

const LoadingSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-14 border-b border-neutral-200 dark:border-neutral-800 px-8 flex items-center gap-4">
      <div className="h-4 w-20 bg-neutral-200 dark:bg-neutral-700 rounded" />
      <div className="h-4 w-px bg-neutral-200 dark:bg-neutral-700" />
      <div className="h-4 w-40 bg-neutral-200 dark:bg-neutral-700 rounded" />
      <div className="h-5 w-16 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
      <div className="flex-1" />
      <div className="h-3 w-32 bg-neutral-200 dark:bg-neutral-700 rounded" />
    </div>
    <div className="h-11 border-b border-neutral-200 dark:border-neutral-800 px-6 flex items-center gap-6">
      <div className="h-3 w-16 bg-neutral-200 dark:bg-neutral-700 rounded" />
    </div>
    <div className="p-6 space-y-4">
      {[1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className="h-14 bg-neutral-200 dark:bg-neutral-700 rounded-lg max-w-lg mx-auto"
        />
      ))}
    </div>
  </div>
);

const NotFound = () => (
  <div className="flex flex-col items-center justify-center py-24">
    <ExclamationCircleIcon className="size-12 text-neutral-300 dark:text-neutral-600" />
    <h3 className="mt-3 text-sm font-semibold text-neutral-900 dark:text-white">
      Execution not found
    </h3>
    <Link
      to="/app/workflows/executions"
      className="mt-2 text-sm text-primary-600 dark:text-primary-400 hover:underline"
    >
      Back to executions
    </Link>
  </div>
);

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export const WorkflowExecutionDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = React.useState('workflow');
  const [selectedTaskId, setSelectedTaskId] = React.useState<string | null>(null);

  const { data: queryData, loading: queryLoading } = useGetWorkflowExecutionQuery({
    client,
    variables: { id: id! },
    skip: !id
  });

  const version = queryData?.workflowExecution?.version ?? 1;

  const { data: subData, error: subError } = useSubscription<
    WatchWorkflowExecutionSubscription,
    WatchWorkflowExecutionSubscriptionVariables
  >(WatchWorkflowExecutionDocument, {
    variables: { id: id!, version },
    client,
    skip: !id || queryLoading
  });

  const execution = subData?.watchWorkflowExecution ?? null;

  const tasks = React.useMemo(() => execution?.tasks ?? [], [subData]);

  const outputs = React.useMemo(() => execution?.outputs ?? [], [subData]);

  const displayArtifacts = React.useMemo<DisplayArtifact[]>(() => {
    const result: DisplayArtifact[] = [];
    for (const output of outputs) {
      const a = output.artifact;
      if (!a) continue;
      if (a.markdown?.content || a.file) {
        result.push({ id: output.id, name: a.name || output.label, artifact: a });
      }
    }
    return result;
  }, [outputs]);

  const selectedTask = React.useMemo(
    () => (selectedTaskId ? (tasks.find((t) => t.id === selectedTaskId) ?? null) : null),
    [selectedTaskId, tasks]
  );

  const activeArtifact = React.useMemo(() => {
    if (!activeTab.startsWith('output-')) return null;
    const outputId = activeTab.replace('output-', '');
    return displayArtifacts.find((a) => a.id === outputId) ?? null;
  }, [activeTab, displayArtifacts]);

  if (!id) return <NotFound />;

  if (queryLoading && !execution) return <LoadingSkeleton />;

  if (!queryLoading && !queryData?.workflowExecution && !execution) return <NotFound />;

  return (
    <div className="-mx-8 sm:-mx-12 -mt-8 sm:-mt-12 -mb-8 sm:-mb-12 flex flex-col h-screen">
      <DetailHeader execution={execution} id={id} />

      {subError && (
        <div className="bg-red-50 dark:bg-red-900/10 border-b border-red-200 dark:border-red-800 px-8 py-3 flex items-center gap-3">
          <p className="text-sm text-red-700 dark:text-red-300">
            Connection error: {subError.message}
          </p>
        </div>
      )}

      <SubNavTabs activeTab={activeTab} onTabChange={setActiveTab} artifacts={displayArtifacts} />

      <div className="flex-1 flex min-h-0 bg-neutral-50 dark:bg-neutral-950">
        <div
          className={clsx(
            'flex-1 min-w-0',
            activeTab === 'workflow' ? 'flex flex-col' : 'p-6 overflow-y-auto'
          )}
        >
          {activeTab === 'workflow' ? (
            <TaskDAG
              tasks={tasks}
              selectedTaskId={selectedTaskId}
              onSelectTask={setSelectedTaskId}
            />
          ) : activeArtifact ? (
            <OutputTabContent artifact={activeArtifact} />
          ) : null}
        </div>

        {selectedTask && activeTab === 'workflow' && (
          <TaskDetailPane
            task={selectedTask}
            allTasks={tasks}
            onClose={() => setSelectedTaskId(null)}
          />
        )}
      </div>
    </div>
  );
};
